import React from 'react';
import SecurityService from '../../utils/securityService';

/**
 * Error Boundary para capturar y manejar errores de React
 * Proporciona una interfaz de usuario fallback cuando ocurren errores
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Actualiza el state para mostrar la interfaz de error
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Generar ID único para el error
        const errorId = Date.now().toString(36) + Math.random().toString(36).substr(2);
        
        // Actualizar state con información del error
        this.setState({
            error: error,
            errorInfo: errorInfo,
            errorId: errorId
        });

        // Log del error de seguridad
        SecurityService.logSecurityEvent('ERROR_BOUNDARY_TRIGGERED', {
            errorId: errorId,
            error: error.toString(),
            errorStack: error.stack,
            componentStack: errorInfo.componentStack,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        });

        // Log en consola para desarrollo
        console.error('Error capturado por ErrorBoundary:', error, errorInfo);

        // En producción, podrías enviar esto a un servicio de logging
        if (process.env.NODE_ENV === 'production') {
            this.reportErrorToService(error, errorInfo, errorId);
        }
    }

    reportErrorToService = (error, errorInfo, errorId) => {
        // Aquí podrías enviar el error a un servicio como Sentry, LogRocket, etc.
        try {
            // Ejemplo de estructura para envío a servicio de logging
            const errorReport = {
                errorId: errorId,
                message: error.toString(),
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: window.location.href,
                userId: this.getUserId(),
                buildVersion: process.env.REACT_APP_VERSION || 'unknown'
            };

            // Aquí harías la petición al servicio de logging
            console.log('Error report para servicio externo:', errorReport);
        } catch (reportError) {
            console.error('Error al reportar error:', reportError);
        }
    };

    getUserId = () => {
        try {
            // Intentar obtener el ID del usuario de la sesión
            const userSession = JSON.parse(localStorage.getItem('userSession') || '{}');
            return userSession.userId || 'anonymous';
        } catch {
            return 'anonymous';
        }
    };

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Interfaz de error personalizada
            return (
                <div style={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f7fafc',
                    padding: '2rem'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '3rem',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        maxWidth: '500px',
                        width: '100%',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '4rem',
                            marginBottom: '1rem'
                        }}>
                            🚨
                        </div>
                        
                        <h2 style={{
                            color: '#e53e3e',
                            fontSize: '1.5rem',
                            marginBottom: '1rem',
                            fontWeight: '600'
                        }}>
                            ¡Oops! Algo salió mal
                        </h2>
                        
                        <p style={{
                            color: '#4a5568',
                            marginBottom: '1.5rem',
                            lineHeight: '1.5'
                        }}>
                            Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado 
                            y está trabajando para solucionarlo.
                        </p>

                        {this.state.errorId && (
                            <div style={{
                                backgroundColor: '#f7fafc',
                                padding: '0.75rem',
                                borderRadius: '6px',
                                marginBottom: '1.5rem',
                                fontSize: '0.875rem',
                                color: '#4a5568'
                            }}>
                                <strong>ID de Error:</strong> {this.state.errorId}
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            gap: '1rem',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <button
                                onClick={this.handleRetry}
                                style={{
                                    backgroundColor: '#2BB67E',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#22a05e'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#2BB67E'}
                            >
                                Intentar de nuevo
                            </button>
                            
                            <button
                                onClick={this.handleReload}
                                style={{
                                    backgroundColor: '#4299e1',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#3182ce'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#4299e1'}
                            >
                                Recargar página
                            </button>
                        </div>

                        {/* Mostrar detalles del error en desarrollo */}
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details style={{
                                marginTop: '2rem',
                                textAlign: 'left',
                                backgroundColor: '#fed7d7',
                                padding: '1rem',
                                borderRadius: '6px'
                            }}>
                                <summary style={{
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    color: '#c53030',
                                    marginBottom: '0.5rem'
                                }}>
                                    Detalles del error (Solo en desarrollo)
                                </summary>
                                <pre style={{
                                    fontSize: '0.75rem',
                                    overflow: 'auto',
                                    maxHeight: '200px',
                                    color: '#c53030'
                                }}>
                                    {this.state.error.toString()}
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        // Si no hay error, renderizar los children normalmente
        return this.props.children;
    }
}

export default ErrorBoundary;